import React from 'react';
import { DateRange, DayPicker, DayPickerProps } from 'react-day-picker';
import dayjs, { Dayjs } from 'dayjs';
import {
    COMMON_DAY_PICKER_PROPS,
    DayPickerField,
    buildBoundMatchers,
    useDatePickerPopoverState,
    useDayPickerLocale,
    useDropdownMonthRange,
} from './datePickerShared';
import { nextRange } from './dateRangeSelection';
import 'react-day-picker/style.css';

export interface DateRangeFieldProps {
    from: Dayjs | null;
    to: Dayjs | null;
    onChange: (from: Dayjs | null, to: Dayjs | null) => void;
    // Floating label, matches the MUI-input look of `SingleDatePicker` so the
    // trigger sits flush with the surrounding Tailwind fields.
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    disablePast?: boolean;
    // Allowlists in YYYY-MM-DD. When set, only listed days are selectable for
    // that endpoint. `allowedStartDays` gates picking the start; once a start
    // is chosen `allowedEndDays` gates picking the end. Wrappers translate any
    // inbound format to YYYY-MM-DD.
    allowedStartDays?: string[];
    allowedEndDays?: string[];
    // Extra explicit bounds on top of the allowlists / disablePast.
    minDate?: Dayjs;
    maxDate?: Dayjs;
    className?: string;
}

type Phase = 'start' | 'end' | 'complete';

// Single-popover date-range field for the booking-create / edit forms.
// Replaces the two separate "check-in date" / "check-out date" pickers with
// one "Booking period" trigger. Built on react-day-picker's range mode with
// the standard three-rule click model everyone knows from booking.com (see
// `handleSelect`).
//
// API stays in Dayjs so callsites keep dispatching Dayjs to Redux. The
// dayjs <-> Date conversion normalises to start-of-day to avoid timezone
// shifts on all-day dates — matching `SingleDatePicker`.
export function DateRangeField(props: DateRangeFieldProps) {
    const state = useDatePickerPopoverState(props.disabled);

    const locale = useDayPickerLocale();

    const startSet = React.useMemo(
        () => (props.allowedStartDays?.length ? new Set(props.allowedStartDays) : null),
        [props.allowedStartDays],
    );
    const endSet = React.useMemo(
        () => (props.allowedEndDays?.length ? new Set(props.allowedEndDays) : null),
        [props.allowedEndDays],
    );

    const phase: Phase = React.useMemo(() => {
        if (props.from && props.to) return 'complete';
        if (props.from) return 'end';
        return 'start';
    }, [props.from, props.to]);

    // Which allowlist gates selection right now. While picking the end we use
    // the end allowlist (plus earlier start days, so rule 2 can restart). In
    // every other phase the next click begins a fresh selection, so we gate on
    // the start allowlist.
    const allowedMatcher = React.useMemo(() => {
        const fromDay = props.from ? props.from.startOf('day') : null;
        const isAllowed = (date: Date): boolean => {
            const key = dayjs(date).format('YYYY-MM-DD');
            if (phase === 'end') {
                if (endSet ? endSet.has(key) : true) return true;
                // Days before the current start stay selectable as a new
                // start — `handleSelect` restarts the selection on them.
                // Without this the end allowlist (always after the start)
                // locked the user in once a too-late start was picked.
                return (
                    (startSet ? startSet.has(key) : true)
                    && !!fromDay
                    && dayjs(date).isBefore(fromDay)
                );
            }
            return startSet ? startSet.has(key) : true;
        };
        return (date: Date) => !isAllowed(date);
    }, [phase, startSet, endSet, props.from]);

    const disabledMatchers = React.useMemo(() => {
        const matchers = buildBoundMatchers(props);
        if (startSet || endSet) matchers.push(allowedMatcher);
        return matchers;
    }, [props.disablePast, props.minDate, props.maxDate, startSet, endSet, allowedMatcher]);

    const { startMonth: dropdownStart, endMonth: dropdownEnd } =
        useDropdownMonthRange(props.minDate, props.maxDate, 1, 2);

    // Click handling delegates to the pure `nextRange` model (react-day-picker
    // v9 passes the triggering date as the 2nd onSelect arg). No endpoint or
    // allowlist special-casing lives here: the `disabled` matcher (phase-gated
    // allowlists) already blocks any day that isn't a legal start or end, so an
    // enabled click is always one of `nextRange`'s three cases. A same-day
    // click completes a single-day range (valid for venues / hourly blockers;
    // apartments disable the same day via `allowedEndDays`).
    const handleSelect = (_range: DateRange | undefined, triggerDate: Date) => {
        const { from, to } = nextRange(dayjs(triggerDate), props.from, props.to);
        props.onChange(from, to);
        if (from && to) {
            state.close();
        }
    };

    const fmt = (d: Dayjs) => d.format('DD MMM YYYY');
    const hasValue = !!props.from || !!props.to;
    const summary = props.from || props.to
        ? `${props.from ? fmt(props.from) : '…'} – ${props.to ? fmt(props.to) : '…'}`
        : '';
    const triggerText = summary || (!props.label && props.placeholder) || ' ';

    const selected: DateRange | undefined = props.from
        ? { from: props.from.toDate(), to: props.to ? props.to.toDate() : undefined }
        : undefined;

    return (
        <DayPickerField
            state={state}
            text={triggerText}
            hasValue={hasValue}
            label={props.label}
            disabled={props.disabled}
            className={props.className}
        >
            <DayPicker
                {...({
                    mode: 'range',
                    selected,
                    onSelect: handleSelect,
                    locale,
                    disabled: disabledMatchers,
                    startMonth: dropdownStart,
                    endMonth: dropdownEnd,
                    ...COMMON_DAY_PICKER_PROPS,
                } as DayPickerProps)}
            />
        </DayPickerField>
    );
}

export default DateRangeField;
